/**
 * projeBilgiDuzenle.js — pure rules behind the stand detail's "Proje Bilgilerini Düzenle" window
 * (backlog #210). No React, no services - tested in projeBilgiDuzenle.test.js.
 *
 * What staff may edit (Bilge, 2026-10-24): the stand type, the height band and the accepted height of the
 * project (PUT /projects/{id}: standTypeId, standHeightId, projectHeightNumber). The deal status, the finance
 * approval and the Serin Proje flag are NOT editable here. The exhibitor's declared area / kW / glass / paint
 * have no endpoint and stay read-only.
 *
 * ⚠️ The backend re-resolves the project's required documents when the type or the band changes and drops the
 * approvals of titles that are no longer required (ProjectService.reResolveRequiredFiles), so the window warns.
 */

/** "4,50" or "4.5" -> 4.5; "" -> null (= leave the height alone); anything else -> NaN (= invalid). */
export function parseHeight(text) {
  if (text == null) return null;
  const s = String(text).trim().replace(",", ".");
  if (s === "") return null;
  if (!/^\d+(\.\d{1,2})?$/.test(s)) return NaN;
  const n = Number(s);
  return n > 0 && n <= 99 ? n : NaN;
}

/** The shown form of a height ("4.5" -> "4,5") - the project stores a number. */
export function heightToText(value) {
  if (value == null || value === "") return "";
  return String(value);
}

/**
 * The PUT body: only what changed (the backend leaves a null field alone). `original` is the project
 * { standTypeId, standHeightId, projectHeightNumber }; `typeId` / `bandId` are the picked ones.
 */
export function buildProjectUpdateBody({ original, typeId, bandId, heightText }) {
  const body = {};
  const o = original || {};
  if (typeId != null && typeId !== "" && Number(typeId) !== Number(o.standTypeId)) body.standTypeId = Number(typeId);
  // A new type always travels with the band picked for it (the old band id may not belong to the new type).
  if (bandId != null && bandId !== "" && (body.standTypeId != null || Number(bandId) !== Number(o.standHeightId))) {
    body.standHeightId = Number(bandId);
  }
  const h = parseHeight(heightText);
  if (h != null && !Number.isNaN(h) && h !== Number(o.projectHeightNumber)) body.projectHeightNumber = h;
  return body;
}

/** Changing the type or the band makes the backend recalculate the required documents. */
export function recalculatesDocuments(body) {
  return body.standTypeId != null || body.standHeightId != null;
}

/**
 * What stops a save: null when it can go. `bands` are the bands of the picked type (a type with no band
 * cannot be a project's type - the filing gate wants both a type and a band).
 */
export function validateProjectEdit({ original, typeId, bandId, bands, heightText }) {
  const h = parseHeight(heightText);
  if (Number.isNaN(h)) return "height";
  const body = buildProjectUpdateBody({ original, typeId, bandId, heightText });
  if (Object.keys(body).length === 0) return "unchanged";
  if (body.standTypeId != null || body.standHeightId != null) {
    if (!Array.isArray(bands) || bands.length === 0) return "noBands";
    const picked = bandId != null && bandId !== "" ? Number(bandId) : null;
    if (picked == null) return "band";
    if (!bands.some((b) => Number(b.stand_height_id) === picked)) return "band";
  }
  return null;
}
